/**
 * DateRangeFilter — filtr zakresu dat dla ekranu Faktury (Agent 1).
 *
 * Opcje:
 *   - Ostatnie 7/30/90 dni
 *   - Bieżący miesiąc / Poprzedni miesiąc
 *   - Własny zakres (data od / data do)
 *
 * Zwraca przez `onApply({ date_from, date_to })` w formacie YYYY-MM-DD.
 */
import { useState } from "react";
import { Calendar } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PRESETS = [
  { value: "7d", label: "Ostatnie 7 dni" },
  { value: "30d", label: "Ostatnie 30 dni" },
  { value: "90d", label: "Ostatnie 90 dni" },
  { value: "current_month", label: "Bieżący miesiąc" },
  { value: "previous_month", label: "Poprzedni miesiąc" },
  { value: "custom", label: "Własny zakres" },
];

const toISO = (d) => d.toISOString().slice(0, 10);

const computeRange = (preset, customFrom, customTo) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (preset === "7d" || preset === "30d" || preset === "90d") {
    const days = parseInt(preset, 10);
    const from = new Date(today);
    from.setDate(from.getDate() - days + 1);
    return { date_from: toISO(from), date_to: toISO(today) };
  }
  if (preset === "current_month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { date_from: toISO(from), date_to: toISO(to) };
  }
  if (preset === "previous_month") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { date_from: toISO(from), date_to: toISO(to) };
  }
  if (preset === "custom") {
    return { date_from: customFrom || "", date_to: customTo || "" };
  }
  return { date_from: "", date_to: "" };
};

const labelFor = (preset) => {
  const p = PRESETS.find((x) => x.value === preset);
  return p ? p.label : "Zakres dat";
};

const DateRangeFilter = ({ value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(value?.preset || "30d");
  const [customFrom, setCustomFrom] = useState(value?.date_from || "");
  const [customTo, setCustomTo] = useState(value?.date_to || "");

  const handleApply = () => {
    const range = computeRange(preset, customFrom, customTo);
    onApply({ preset, ...range });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full sm:w-auto h-12 sm:h-10 justify-start gap-2"
          data-testid="date-range-filter-btn"
        >
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="truncate">{labelFor(value?.preset || preset)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 space-y-3"
        data-testid="date-range-filter-popover"
      >
        <div className="text-sm font-semibold text-gray-900">📅 Zakres faktur</div>
        <div className="space-y-1.5">
          {PRESETS.map((p) => (
            <label
              key={p.value}
              className="flex items-center gap-2 cursor-pointer select-none"
              data-testid={`date-range-preset-${p.value}`}
            >
              <input
                type="radio"
                name="date-range-preset"
                value={p.value}
                checked={preset === p.value}
                onChange={() => setPreset(p.value)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">{p.label}</span>
            </label>
          ))}
        </div>

        {preset === "custom" && (
          <div className="space-y-2 pt-1 border-t border-gray-100">
            <div>
              <div className="text-xs text-gray-500 mb-1">Data od</div>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                data-testid="date-range-custom-from"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Data do</div>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                data-testid="date-range-custom-to"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button
            size="sm"
            onClick={handleApply}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="date-range-apply-btn"
          >
            Zastosuj
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeFilter;
